/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Component, OnInit } from '@angular/core';
import { BuilderUserContext } from '@gh/builder-shared';
import {
  BuilderUserProfileValidationDto,
  PayPalAcceptanceDto,
  PayPalRestService,
  ValidationStatus,
} from '@gh/builder-shared-data';
import { HelpID } from '@gh/common-shared';
import { Enum } from '@gh/core-data';
import { TranslationService } from '@gh/core-mls';
import { DialogInput, splitByLines, WaitingService } from '@gh/core-ui';
import { fromPairs, isString } from 'lodash';

export type UserProfileValidationMessage = {
  header: string;
  body?: string;
  footer?: string;
};

@Component({
  selector: 'gh-user-profile-validation-dialog',
  template: require('./user-profile-validation-dialog.component.html'),
  styles: [require('./user-profile-validation-dialog.component.scss')],
})
export class UserProfileValidationDialogComponent implements OnInit, DialogInput<BuilderUserProfileValidationDto> {
  message: UserProfileValidationMessage;
  input: BuilderUserProfileValidationDto;
  helpId: HelpID;

  constructor(private userContext: BuilderUserContext,
              private translationService: TranslationService,
              private waitingService: WaitingService,
              private paypalRest: PayPalRestService) {

  }

  get title(): string {
    // @ts-ignore
    return {
      [ValidationStatus.LoginDenied]: 'msg_login_denied',
      [ValidationStatus.SessionWasExpired]: 'msg_session_was_expired',
      [ValidationStatus.OfficeIsNotActivated]: 'lbl_paypal_office_not_activated',
      [ValidationStatus.AcceptAgreement]: 'lbl_paypal_activate',
      [ValidationStatus.PaymentInProcess]: 'lbl_paypal_payment',
      [ValidationStatus.CheckPayPalAccount]: 'lbl_paypal_overdue_payment',
    }[this.input.paymentStatus];
  }

  get hasPayPalLink(): boolean {
    return this.input.paymentStatus === ValidationStatus.AcceptAgreement;
  }

  ngOnInit(): void {
    this.message = this.getMessage(Enum.toValue(ValidationStatus, this.input.paymentStatus));

    if (this.needsHelpId()) {
      this.helpId = 'common/first-franchisee-login';
    }
  }

  onLogout(): void {
    this.userContext.logout();
  }

  goToPayPal(): void {
    this.paypalRest.getAcceptanceLink().pipe(
      this.waitingService.operator<PayPalAcceptanceDto>())
      .subscribe((link) => {
        window.location.href = link.acceptanceLink;
      });
  }

  private getMessage(messageId: string): UserProfileValidationMessage {
    const raw = this.translationService.getRaw(messageId);

    if (isString(raw)) {
      return { header: splitByLines(this.translationService.get(messageId)) };
    } else {
      return <UserProfileValidationMessage>fromPairs(['header', 'body', 'footer'].filter((key) => raw[key])
        .map((key) => [key, splitByLines(this.translationService.get(`${messageId}.${key}`, this.input))]));
    }
  }

  private needsHelpId(): boolean {
    const { paymentStatus } = this.input;
    switch (paymentStatus) {
      case ValidationStatus.LoginDenied:
      case ValidationStatus.SessionWasExpired:
        return false;
      default:
        return true;
    }
  }
}
